import type { ProductOptionOverride } from '@/types'

/**
 * Per-item prices on a shared option list (F-MOD-N13) — the editor's state.
 *
 * One "Hot or Iced" list can be shared by every drink and still charge +60 on
 * the Caramel and +120 on the Latte. The owner types the item's own price next
 * to the shared one; blank means "use the shared price". Pure functions here,
 * the component only renders them.
 */

export interface OptionPriceRow {
  optionId: string
  optionName: string
  groupId: string
  groupName: string
  priceMode: 'add' | 'replace'
  sharedPrice: number
  sharedAvailable: boolean
  /** Text in the price box: '' = shared price. */
  priceText: string
  /** false = not offered on this item. Can't revive a shared sold-out. */
  offered: boolean
  groupProductCount: number
  hasOwnRecipe: boolean
  /** Server value, kept so a save sends cost_delta back untouched. */
  costDelta: number | null
}

export function rowsFromServer(views: ProductOptionOverride[]): OptionPriceRow[] {
  return views.map((v) => ({
    optionId: v.option_id,
    optionName: v.option_name,
    groupId: v.group_id,
    groupName: v.group_name,
    priceMode: v.price_mode,
    sharedPrice: v.shared_price_delta,
    sharedAvailable: v.shared_is_available,
    priceText: v.price_delta == null ? '' : String(v.price_delta),
    offered: v.is_available !== false,
    groupProductCount: v.group_product_count,
    hasOwnRecipe: v.has_own_recipe,
    costDelta: v.cost_delta,
  }))
}

/** The price the item will actually charge for the option, or null when the box is invalid. */
export function effectivePrice(r: OptionPriceRow): number | null {
  const t = r.priceText.trim()
  if (t === '') return r.sharedPrice
  const n = Number(t)
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : null
}

export function rowProblem(r: OptionPriceRow): string | null {
  return effectivePrice(r) === null ? `“${r.optionName}” needs a price of 0 or more, or leave it blank for the shared price.` : null
}

/**
 * The PUT body: one row per option that differs from the shared value. A
 * price equal to the shared price is not an override — storing it would make
 * a later change to the shared price silently skip this item.
 */
export function overridesPayload(rows: OptionPriceRow[]): Array<{ option_id: string; price_delta?: number; cost_delta?: number; is_available?: boolean }> {
  const out: Array<{ option_id: string; price_delta?: number; cost_delta?: number; is_available?: boolean }> = []
  for (const r of rows) {
    const p = effectivePrice(r)
    const row: { option_id: string; price_delta?: number; cost_delta?: number; is_available?: boolean } = { option_id: r.optionId }
    if (p !== null && r.priceText.trim() !== '' && p !== r.sharedPrice) row.price_delta = p
    if (!r.offered) row.is_available = false
    if (r.costDelta != null) row.cost_delta = r.costDelta
    if (row.price_delta !== undefined || row.is_available !== undefined || row.cost_delta !== undefined) out.push(row)
  }
  return out
}

export interface PriceChange {
  label: string
  before: string
  after: string
}

/**
 * Every guest-facing difference a save would make on this item, for the
 * confirm step. Customer-visible money is stop-and-ask — the owner sees the
 * before and after of each option before anything is written.
 */
export function priceChanges(before: OptionPriceRow[], after: OptionPriceRow[], fmt: (n: number) => string): PriceChange[] {
  const prev = new Map(before.map((r) => [r.optionId, r]))
  const out: PriceChange[] = []
  for (const r of after) {
    const b = prev.get(r.optionId)
    if (!b) continue
    const label = `${r.groupName} · ${r.optionName}`
    const pb = effectivePrice(b)
    const pa = effectivePrice(r)
    const show = (p: number | null, mode: 'add' | 'replace') => (p === null ? '—' : mode === 'replace' ? fmt(p) : `+${fmt(p)}`)
    if (pb !== pa) out.push({ label, before: show(pb, b.priceMode), after: show(pa, r.priceMode) })
    if (b.offered !== r.offered) out.push({ label, before: b.offered ? 'Offered' : 'Not offered', after: r.offered ? 'Offered' : 'Not offered' })
  }
  return out
}
